/**
 * Video service API endpoints
 */

import { videoApi } from "./client";
import { noteCallDiagnostics } from "../modules/call/store/callDiagnosticsStore";
import type {
  Room,
  RoomsResponse,
  CreateRoomRequest,
  RecordingsResponse,
} from "./types/video";
import type { ListParams } from "./types/common";

export const videoService = {
  /**
   * Get all rooms
   */
  getRooms: async (params?: ListParams): Promise<RoomsResponse> => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return videoApi.get(`/rooms/${query ? `?${query}` : ""}`);
  },

  /**
   * Get single room
   */
  getRoom: async (roomId: string): Promise<Room> => {
    return videoApi.get(`/rooms/${roomId}/`);
  },

  /**
   * Create new room
   */
  createRoom: async (request: CreateRoomRequest): Promise<Room> => {
    return videoApi.post("/rooms/", request);
  },

  /**
   * Join room
   */
  joinRoom: async (roomId: string): Promise<{ token: string }> => {
    return videoApi.post(`/rooms/${roomId}/join/`, {});
  },

  /**
   * Leave room
   */
  leaveRoom: async (roomId: string): Promise<void> => {
    return videoApi.post(`/rooms/${roomId}/leave/`, {});
  },

  /**
   * Get recordings for room
   */
  getRecordings: async (roomId: string, params?: ListParams): Promise<RecordingsResponse> => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return videoApi.get(`/rooms/${roomId}/recordings/${query ? `?${query}` : ""}`);
  },

  /**
   * Delete room
   */
  deleteRoom: async (roomId: string): Promise<void> => {
    return videoApi.delete(`/rooms/${roomId}/`);
  },
};

export interface GuestRoomInfo {
  name: string;
  host_present: boolean;
}

export interface GuestJoinResponse {
  participant_id: string;
  display_name: string;
  livekit: {
    url: string;
    room: string;
    token: string;
    call_diagnostics?: boolean;
  };
  /** True when the room's admit_policy was "participants" at join time
   *  (#1850). Optional: sessions stored before the field existed lack it. */
  may_admit_guests?: boolean;
}

export const guestRoomService = {
  lookup: async (joinCode: string): Promise<GuestRoomInfo> => {
    return videoApi.get(`/guest-rooms/${joinCode}/`, { skipAuth: true });
  },

  join: async (
    joinCode: string,
    payload: { name: string; company: string }
  ): Promise<GuestJoinResponse> => {
    return noteCallDiagnostics(
      await videoApi.post<GuestJoinResponse>(`/guest-rooms/${joinCode}/join/`, payload, {
        skipAuth: true,
      }),
    );
  },

  /** Signed-in invitee: joins under their account, no name/company (GH-1915). */
  joinAsAccount: async (joinCode: string): Promise<GuestJoinResponse> => {
    return noteCallDiagnostics(await videoApi.post<GuestJoinResponse>(`/guest-rooms/${joinCode}/join/`, {}));
  },
};
